import { create } from 'jss'
import preset from 'jss-preset-default'
import mainPalette from './palette'
import typography from './typography'
import resetCss from './reset'
import fontRegular from '../assets/fonts/arsmaquettepro-regular.woff2'
import fontMedium from '../assets/fonts/arsmaquettepro-medium.woff2'
import fontBold from '../assets/fonts/arsmaquettepro-bold.woff2'
// Currently supported theme names ['light', 'dark']

export const getAppTheme = ({ name = 'light', direction = 'ltr' } = {}) => {
    const palette = mainPalette[name]
    const spacing = 8
    const breakpoints = {
        width: {
            xxxs: 320,
            xxs: 360,
            xs: 375,
            s: 414,
            m: 768,
            l: 1024,
            xl: 1366,
            xxl: 1440,
            xxxl: 1536,
            xxxxl: 1920,
        },
        height: {
            xxxs: 640,
            xxs: 667,
            xs: 736,
            s: 812,
            m: 864,
            l: 896,
            xl: 900,
            xxl: 926,
            xxxl: 962,
            xxxxl: 1080,
        },
    }
    const mediaQueries = {
        xxsUp: `@media only screen and (min-width: ${breakpoints.width.xxs}px)`,
        xxsUpH: `@media only screen and (min-width: ${breakpoints.width.xxs}px) and (min-height: 540px)`,
        xsUp: `@media only screen and (min-width: ${breakpoints.width.xs}px)`,
        xsUpH: `@media only screen and (min-width: ${breakpoints.width.xs}px) and (min-height: 600px)`,
        sUp: `@media only screen and (min-width: ${breakpoints.width.s}px)`,
        sUpM: `@media only screen and (min-width: 600px)`,
        mUp: `@media only screen and (min-width: ${breakpoints.width.m}px)`,
        lUp: `@media only screen and (min-width: ${breakpoints.width.l}px)`,
        xlUp: `@media only screen and (min-width: ${breakpoints.width.xl}px)`,
        xxlUp: `@media only screen and (min-width: ${breakpoints.width.xxl}px)`,
        xxxlUp: `@media only screen and (min-width: ${breakpoints.width.xxxl}px)`,
        xxxxlUp: `@media only screen and (min-width: ${breakpoints.width.xxxxl}px)`,

        xxs: `@media only screen and (max-width: ${breakpoints.width.xxs}px)`,
        s: `@media only screen and (max-width: ${breakpoints.width.s}px)`,
        m: `@media only screen and (max-width: ${breakpoints.width.m}px)`,
    }
    const zIndex = {
        modal: 1300,
        modalOverlay: 1299,
        snackbar: 1400,
        tooltip: 1500,
    }
    const sizing = {
        headerHeight: 80,
        headerHeightMobile: 72,
    }
    const controls = {
        label: {
            position: 'relative',
            color: palette.grey[600],
            margin: [16, 0],
        },
        input: {
            letterSpacing: 0.5,
            boxSizing: 'border-box',
            position: 'relative',
            width: '100%',
            border: `2px solid`,
            borderColor: palette.grey[300],
            borderRadius: 2,
            color: palette.input.active,
            padding: spacing * 2,
            outline: 'none',
            textAlign: 'left',
            '&::placeholder': {
                color: palette.input.placeholder,
            },
            '&:hover': {
                borderColor: palette.grey[400],
            },
            '&:focus': {
                borderColor: palette.primary.main,
            },
            '&[readonly], &[disabled]': {
                cursor: 'default',
                border: `none`,
                backgroundColor: palette.disabled.light,
                color: palette.grey[400],
            },
        },
        inputError: {
            extend: 'input',
            borderColor: palette.error.light,
            '& svg': {
                color: palette.error.main,
            },
            '&:focus': {
                borderColor: palette.error.light,
            },
        },
        inputSuccess: {
            extend: 'input',
            borderColor: palette.success.light,
            '& svg': {
                color: palette.success.main,
            },
            '&:focus': {
                borderColor: palette.success.light,
            },
        },
        inputWarning: {
            extend: 'input',
            borderColor: palette.warning.light,
            '& svg': {
                color: palette.warning.main,
            },
            '&:focus': {
                borderColor: palette.warning.light,
            },
        },
        textarea: {
            border: `2px solid`,
            borderColor: palette.grey[300],
        },
        textareaError: {
            extend: 'textarea',
            borderColor: palette.error.light,
            '& svg': {
                color: palette.error.main,
            },
            '&:focus': {
                borderColor: palette.error.light,
            },
        },
        small: {
            color: palette.grey[500],
        },
    }
    const alerts = {
        default: {
            color: palette.tertiary.main,
            backgroundColor: palette.tertiary.background,
            borderColor: palette.tertiary.light,
        },
        success: {
            color: palette.success.main,
            backgroundColor: palette.success.background,
            borderColor: palette.success.light,
        },
        error: {
            color: palette.error.main,
            backgroundColor: palette.error.background,
            borderColor: palette.error.light,
        },
        warning: {
            color: palette.warning.main,
            backgroundColor: palette.warning.background,
            borderColor: palette.warning.light,
        },
    }

    const grid = {
        centered: {
            display: 'grid',
            alignItems: 'center',
            justifyContent: 'center',
        },
        start: {
            display: 'grid',
            alignItems: 'center',
            justifyContent: 'start',
        },
        end: {
            display: 'grid',
            alignItems: 'center',
            justifyContent: 'end',
        },
        colFlow: {
            gridAutoFlow: 'column',
            gridColumnGap: spacing,
        },
        rowFlow: {
            gridAutoFlow: 'row',
            gridRowGap: spacing,
        },
        gapS: {
            gridColumnGap: spacing,
            gridRowGap: spacing,
        },
        gapM: {
            gridColumnGap: spacing * 2,
            gridRowGap: spacing * 2,
        },
        gapL: {
            gridColumnGap: spacing * 4,
            gridRowGap: spacing * 4,
        },
        noGap: {
            gridColumnGap: 0,
            gridRowGap: 0,
        },
    }
    const flexbox = {
        centered: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        start: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
        },
        end: {
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
        },
        spaceBetween: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
    }
    const truncateText = {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    }

    const pageStyle = {
        title: {
            color: palette.text.primary,
            fontSize: 28,
            letterSpacing: 0.25,
            fontWeight: typography.fontWeights.bold,
            marginBottom: 8,
        },
        sectionTitle: {
            color: palette.grey[600],
            fontSize: 16,
            letterSpacing: 0.5,
            fontWeight: typography.fontWeights.regular,
            fontFamily: typography.secondaryFontFamily,
        },
        modalTitle: {
            color: palette.secondary.darker,
            fontSize: 18,
            letterSpacing: 0.5,
            fontWeight: typography.fontWeights.bold,
            fontFamily: typography.secondaryFontFamily,
        },
    }

    const link = {
        cursor: 'pointer',
        textDecoration: 'none',
        color: palette.link.normal,
        fontWeight: 600,
        '&:hover': {
            textDecoration: 'underline',
        },
    }
    const jssInstance = create(preset())

    if (process.env.NODE_ENV === 'production') {
        jssInstance.setup({ id: { minify: true } })
    }
    jssInstance
        .createStyleSheet(
            {
                ...resetCss,
                '@global': {
                    /*  '*': {
                        /*'&::-webkit-scrollbar': {
                              display: 'none',
                          },
                          '-ms-overflow-style':
                              'none' /!* for Internet Explorer, Edge *!/,
                          ' scrollbar-width': 'none' /!* for Firefox *!/,
                          // '-webkit-tap-highlight-color': 'transparent',
                    },*/
                    'html, body, #root': {
                        height: '100%',
                        fontFamily: typography.secondaryFontFamily,
                        fontSize: typography.defaultFontSize,
                        color: palette.text.body,
                        backgroundColor: palette.background.body,
                        '-webkit-font-smoothing': 'antialiased',
                        '-moz-osx-font-smoothing': 'grayscale',
                        minHeight: '100vh',
                    },
                    '@font-face': [
                        {
                            fontFamily: 'ARS Maquette Pro',
                            src: `url(${fontRegular}) format("woff2")`,
                            'font-weight': 'normal',
                            'font-style': 'normal',
                            'font-display': 'swap',
                        },
                        {
                            fontFamily: 'ARS Maquette Pro',
                            src: `url(${fontMedium}) format("woff2")`,
                            'font-weight': '500',
                            'font-style': 'normal',
                            'font-display': 'swap',
                        },
                        {
                            fontFamily: 'ARS Maquette Pro',
                            src: `url(${fontBold}) format("woff2")`,
                            'font-weight': '700',
                            'font-style': 'normal',
                            'font-display': 'swap',
                        },
                    ],
                    body: {

                    },
                    'h1, h2, h3, h4, h5, h6, p, span, small': {
                        marginBottom: 0,
                    },
                    'label, input, textarea, select, button': {
                        fontFamily: 'inherit',
                    },
                    'svg[viewBox="0 0 24 24"]:not([data-color])': {
                        fill: palette.icon.inactive,
                    },
                    label: controls.label,
                    input: controls.input,
                    small: controls.small,
                    textarea: {
                        ...controls.input,
                        overflowWrap: 'break-word',
                    },
                    form: {
                        textAlign: 'left',
                    },
                    '@keyframes spin': {
                        '100%': {
                            '-webkit-transform': 'rotate(360deg)',
                            transform: 'rotate(360deg)',
                        },
                    },
                },
            },
            { index: 1, meta: 'globals' }
        )
        .attach()

    return {
        breakpoints,
        mediaQueries,
        zIndex,
        sizing,
        spacing,
        palette,
        typography,
        controls,
        alerts,
        utils: {
            grid,
            flexbox,
            truncateText,
        },
        pageStyle,
        link,
    }
}
